import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { EMPTY, Subject, Subscription, interval } from 'rxjs';
import { catchError, debounceTime, filter, switchMap, tap } from 'rxjs/operators';

import {
  AnalyticsService,
  AutoZonesPreviewResponse,
  ZoneDeviationNotice,
  ZoneDeviationOverlapSet,
} from '../../analytics/analytics.service';
import {
  ZoneDistributionComponent,
  ZoneRowVm,
} from '../zone-distribution/zone-distribution.component';

export interface ZoneDeviationDialogData {
  notice: ZoneDeviationNotice;
}

@Component({
  selector: 'app-zone-deviation-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    TranslateModule,
    ZoneDistributionComponent,
  ],
  template: `
    <h2 mat-dialog-title>
      {{ 'diagnostics.zone_deviation.dialog.title' | translate }}
      @if (data.notice.athlete_name) {
        <span class="dialog-athlete">— {{ data.notice.athlete_name }}</span>
      }
    </h2>

    <mat-dialog-content class="dialog-content">
      <p class="dialog-subtitle">
        {{ 'diagnostics.zone_deviation.dialog.subtitle' | translate:{ date: detectedAtFormatted() } }}
      </p>

      <table class="anchor-table">
        <thead>
          <tr>
            <th></th>
            <th>{{ 'diagnostics.zone_deviation.dialog.col_current' | translate:{ source: data.notice.effective_source } }}</th>
            <th>{{ 'diagnostics.zone_deviation.dialog.col_auto' | translate:{ date: effectiveFromFormatted() } }}</th>
            <th>Δ</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th>{{ 'diagnostics.zone_deviation.dialog.row_mlss' | translate }}</th>
            <td>{{ data.notice.effective_maxlass_w | number:'1.0-0' }} W</td>
            <td>
              <input class="anchor-input" type="number" min="1" step="1"
                     [disabled]="busy || processing"
                     [ngModel]="maxlassEdit" (ngModelChange)="onAnchorEdit('maxlass', $event)"> W
            </td>
            <td [class.delta-warn]="absPct(maxlassDeltaPct()) >= 5">
              {{ formatSignedPct(maxlassDeltaPct()) }}
            </td>
          </tr>
          <tr>
            <th>{{ 'diagnostics.zone_deviation.dialog.row_vo2max' | translate }}</th>
            <td>{{ data.notice.effective_p_vo2max_w | number:'1.0-0' }} W</td>
            <td>
              <input class="anchor-input" type="number" min="1" step="1"
                     [disabled]="busy || processing"
                     [ngModel]="vo2maxPowerEdit" (ngModelChange)="onAnchorEdit('vo2max', $event)"> W
            </td>
            <td [class.delta-warn]="absPct(vo2maxDeltaPct()) >= 5">
              {{ formatSignedPct(vo2maxDeltaPct()) }}
            </td>
          </tr>
        </tbody>
      </table>
      <p class="dialog-hint">{{ 'diagnostics.zone_deviation.dialog.anchor_edit_hint' | translate }}</p>

      @if (previewLoading) {
        <p class="dialog-hint preview-status">
          <mat-spinner diameter="16"></mat-spinner>
          {{ 'diagnostics.zone_deviation.dialog.preview_loading' | translate }}
        </p>
      } @else if (previewError) {
        <p class="dialog-error">{{ 'diagnostics.zone_deviation.dialog.preview_error' | translate }}</p>
      }

      @if (displayRows().length > 0) {
        <h3 class="dialog-zones-title">
          {{ 'diagnostics.zone_deviation.dialog.col_auto' | translate:{ date: effectiveFromFormatted() } }}
          @if (previewFitQuality != null) {
            <span class="fit-quality">
              · {{ 'diagnostics.zone_deviation.dialog.fit_quality' | translate }}
              {{ previewFitQuality | number:'1.1-1' }}%
            </span>
          }
        </h3>
        <app-zone-distribution [rows]="displayRows()" mode="bounds-only" />
      }

      @if (previewImplausible) {
        <p class="dialog-error zones-implausible">
          <mat-icon>warning</mat-icon>
          {{ 'diagnostics.zone_deviation.dialog.implausible_warning' | translate }}
        </p>
      }

      <div class="dialog-actions-block">
        <mat-form-field appearance="outline" class="dialog-date-field">
          <mat-label>{{ 'diagnostics.zone_deviation.dialog.effective_from' | translate }}</mat-label>
          <input matInput [matDatepicker]="picker" [(ngModel)]="effectiveFrom"
                 (ngModelChange)="onDateChange()" [max]="maxDate"
                 [disabled]="busy || processing">
          <mat-datepicker-toggle matIconSuffix [for]="picker"></mat-datepicker-toggle>
          <mat-datepicker #picker></mat-datepicker>
        </mat-form-field>
        <p class="dialog-date-hint">{{ 'diagnostics.zone_deviation.dialog.effective_from_hint' | translate }}</p>
      </div>

      @if (conflict && conflict.length > 0) {
        <div class="conflict-block">
          <h3 class="conflict-title">
            {{ 'diagnostics.zone_deviation.dialog.conflict_title' | translate }}
          </h3>
          <p class="conflict-body">
            {{ 'diagnostics.zone_deviation.dialog.conflict_body' | translate:{
                  date: conflict[0].test_date, source: conflict[0].source } }}
          </p>
          <div class="conflict-actions">
            <button mat-stroked-button [disabled]="busy || previewImplausible" (click)="applyAuto('keep_later')">
              {{ 'diagnostics.zone_deviation.dialog.conflict_keep_later' | translate }}
            </button>
            <button mat-raised-button color="primary" [disabled]="busy || previewImplausible" (click)="applyAuto('supersede')">
              {{ 'diagnostics.zone_deviation.dialog.conflict_supersede' | translate }}
            </button>
            <button mat-button [disabled]="busy" (click)="conflict = null">
              {{ 'diagnostics.zone_deviation.dialog.conflict_cancel' | translate }}
            </button>
          </div>
        </div>
      }

      @if (processing) {
        <p class="dialog-hint preview-status">
          <mat-spinner diameter="16"></mat-spinner>
          {{ 'diagnostics.zone_deviation.dialog.processing' | translate:{ date: effectiveFromFormatted() } }}
        </p>
      }
      @if (applyError) {
        <p class="dialog-error">{{ 'diagnostics.zone_deviation.dialog.apply_failed' | translate }}</p>
      }
    </mat-dialog-content>

    <mat-dialog-actions class="dialog-footer">
      <a class="dialog-settings-link" (click)="openSettings()">
        {{ 'diagnostics.zone_deviation.dialog.settings_link' | translate }}
      </a>
      <span class="grow"></span>
      <button mat-button [disabled]="busy || processing" (click)="dismiss()">
        {{ 'diagnostics.zone_deviation.dialog.action_dismiss' | translate }}
      </button>
      @if (!conflict) {
        <button mat-raised-button color="primary"
                [disabled]="busy || processing || !effectiveFromValid() || previewImplausible"
                (click)="applyAuto()">
          {{ 'diagnostics.zone_deviation.dialog.action_apply' | translate }}
        </button>
      }
    </mat-dialog-actions>
  `,
  styles: [`
    .dialog-athlete { color: var(--text-secondary, #666); font-weight: 400; margin-left: 0.5rem; }
    .dialog-subtitle { color: var(--text-secondary, #666); margin-top: 0; }
    .dialog-content { min-width: 360px; max-width: 600px; }
    .anchor-table {
      width: 100%; border-collapse: collapse; margin: 0.5rem 0 0.5rem;
      font-variant-numeric: tabular-nums;
    }
    .anchor-table th, .anchor-table td {
      padding: 0.4rem 0.6rem; text-align: left;
      border-bottom: 1px solid var(--mat-sys-outline-variant, #ccd5da);
    }
    .anchor-table thead th { color: var(--text-secondary, #666); font-weight: 500; }
    .anchor-input {
      width: 4.5rem; padding: 0.2rem 0.35rem; text-align: right;
      font: inherit; font-variant-numeric: tabular-nums;
      border: 1px solid var(--mat-sys-outline-variant, #ccd5da); border-radius: var(--radius-sm, 4px);
      background: var(--mat-sys-surface, #fff); color: inherit;
    }
    .anchor-input:focus { outline: none; border-color: var(--mat-sys-primary, #00696e); }
    .delta-warn { color: #c62828; font-weight: 600; }
    .dialog-hint { font-size: var(--fs-xs); color: var(--text-secondary, #666); margin: 0.25rem 0 0; }
    .dialog-error { font-size: var(--fs-sm); color: #c62828; margin: 0.5rem 0 0; }
    .preview-status { display: flex; align-items: center; gap: 0.4rem; }
    .dialog-zones-title { font-size: var(--fs-sm); margin: 1rem 0 0.4rem;
                          color: var(--text-secondary, #666); }
    .fit-quality { font-size: var(--fs-xs); }
    .dialog-actions-block { margin-top: 1rem; }
    .dialog-date-field { width: 100%; max-width: 220px; }
    .dialog-date-hint { font-size: var(--fs-xs); color: var(--text-secondary, #666);
                        margin: 0.25rem 0 0; }
    .conflict-block {
      margin-top: 1rem; padding: 0.75rem; border-radius: var(--radius-md, 8px);
      background: var(--mat-sys-surface-container, #f0f4f5);
      border: 1px solid var(--mat-sys-outline-variant, #ccd5da);
    }
    .conflict-title { font-size: var(--fs-sm); margin: 0 0 0.4rem; }
    .conflict-body { font-size: var(--fs-sm); color: var(--text-secondary, #666); margin: 0 0 0.6rem; }
    .conflict-actions { display: flex; flex-wrap: wrap; gap: 0.5rem; }
    .dialog-footer { display: flex; align-items: center; gap: 0.5rem; }
    .dialog-settings-link { color: var(--text-secondary, #666); cursor: pointer;
                            font-size: var(--fs-sm); text-decoration: underline; }
    .dialog-settings-link:hover { color: var(--text-primary, #222); }
    .grow { flex: 1; }
  `],
})
export class ZoneDeviationDialogComponent implements OnInit, OnDestroy {
  private readonly analytics = inject(AnalyticsService);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly dialogRef = inject(MatDialogRef<ZoneDeviationDialogComponent>);

  effectiveFrom: Date = new Date();
  maxDate: Date = new Date();
  busy = false;
  processing = false;

  /** Editierbare Anker (vorbelegt mit den Auto-Werten). */
  maxlassEdit: number | null = null;
  vo2maxPowerEdit: number | null = null;
  /** True, sobald der Nutzer den jeweiligen Anker manuell ueberschrieben hat.
   *  Nicht-ueberschriebene Anker werden aus jeder Preview re-seedet. */
  private maxlassOverridden = false;
  private vo2maxOverridden = false;

  /** Live-Vorschau-Zustand. */
  previewLoading = false;
  previewError = false;
  previewFitQuality: number | null = null;
  /** Preview-Zonen sind nicht monoton -> Anwenden sperren + Hinweis. */
  previewImplausible = false;
  private previewRows: ZoneRowVm[] | null = null;

  /** Konflikt (Rück-Datierung vor einen späteren Parametersatz). */
  conflict: ZoneDeviationOverlapSet[] | null = null;
  applyError = false;

  private readonly preview$ = new Subject<void>();
  private previewSub?: Subscription;
  private pollSub?: Subscription;

  constructor(@Inject(MAT_DIALOG_DATA) public data: ZoneDeviationDialogData) {}

  ngOnInit(): void {
    // Datum auf reinen Kalendertag normalisieren (ohne Uhrzeit), damit der
    // Vergleich gegen [max] und der ISO-Roundtrip frei von Off-by-one sind.
    this.effectiveFrom = this.startOfDay(new Date());
    this.maxDate = this.startOfDay(new Date());

    // Vorbelegung mit den Detektions-Ankern als Platzhalter, bis die erste
    // Preview am effective_from die tatsaechlichen Auto-Werte liefert.
    this.maxlassEdit = Math.round(this.data.notice.auto_cp_w ?? 0) || null;
    this.vo2maxPowerEdit = Math.round(this.data.notice.auto_cp_6min_w ?? 0) || null;

    // Debouncte, race-sichere Preview-Pipeline: switchMap cancelt in-flight
    // Requests; das innere catchError haelt den Outer-Stream nach einem
    // Netzwerkfehler am Leben (sonst kaeme nie wieder eine Preview).
    this.previewSub = this.preview$
      .pipe(
        debounceTime(400),
        filter(() => this.effectiveFromValid()),
        tap(() => {
          this.previewLoading = true;
          this.previewError = false;
          this.cdr.markForCheck();
        }),
        switchMap(() =>
          this.analytics.previewAutoZones({
            user_id: this.data.notice.user_id,
            effective_from: this.toIsoDate(this.effectiveFrom),
            ...this.overridePayload(),
          }).pipe(
            catchError(() => {
              this.previewLoading = false;
              this.previewError = true;
              this.previewRows = null;
              this.cdr.markForCheck();
              return EMPTY;
            }),
          ),
        ),
      )
      .subscribe((res) => this.applyPreviewSuccess(res));

    // Erste Vorschau (ohne Overrides → Auto-Werte) ueber dieselbe Pipeline.
    this.preview$.next();
  }

  ngOnDestroy(): void {
    this.previewSub?.unsubscribe();
    this.pollSub?.unsubscribe();
  }

  detectedAtFormatted(): string {
    if (!this.data.notice.detected_at) return '—';
    return new Date(this.data.notice.detected_at).toLocaleDateString();
  }

  effectiveFromFormatted(): string {
    return this.effectiveFromValid() ? this.effectiveFrom.toLocaleDateString() : '—';
  }

  /** Reiner Kalendertag (lokale Mitternacht) ohne Uhrzeit-Anteil. */
  private startOfDay(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  /** True nur für ein echtes, gültiges Datum (nicht null / Invalid Date).
   *  Beim Tippen eines unparsbaren Datums liefert der Adapter ein Invalid Date
   *  (truthy!) — ohne diese Prüfung würden runPreview/applyAuto/toIsoDate sonst
   *  "NaN-NaN-NaN" ans Backend schicken. */
  effectiveFromValid(): boolean {
    return this.effectiveFrom instanceof Date && !isNaN(this.effectiveFrom.getTime());
  }

  absPct(v: number | null): number {
    return v == null ? 0 : Math.abs(v);
  }

  formatSignedPct(v: number | null): string {
    if (v == null) return '—';
    const sign = v > 0 ? '+' : (v < 0 ? '' : '±');
    return `${sign}${v.toFixed(1)}%`;
  }

  /** Δ live aus dem angezeigten Auto-CP-Feldwert vs. aktuell wirksamem Anker.
   *  Deckt sowohl den Auto- (re-seedet aus Preview) als auch den Override-Fall ab. */
  maxlassDeltaPct(): number | null {
    const eff = this.data.notice.effective_maxlass_w;
    if (this.maxlassEdit == null || eff == null || eff === 0) return null;
    return (this.maxlassEdit - eff) / eff * 100;
  }

  vo2maxDeltaPct(): number | null {
    const eff = this.data.notice.effective_p_vo2max_w;
    if (this.vo2maxPowerEdit == null || eff == null || eff === 0) return null;
    return (this.vo2maxPowerEdit - eff) / eff * 100;
  }

  /** Vorschau-Zonen falls vorhanden, sonst der ursprüngliche Snapshot. */
  displayRows(): ZoneRowVm[] {
    return this.previewRows ?? this.snapshotRows();
  }

  private snapshotRows(): ZoneRowVm[] {
    return this.mapZones(this.data.notice.auto_zones_snapshot ?? []);
  }

  private mapZones(zones: any[]): ZoneRowVm[] {
    return (zones ?? []).map((z: any) => {
      const lower = z.lower_w;
      const upper = z.upper_w;
      let range: string;
      if (lower == null && upper != null) range = `< ${Math.round(upper)} W`;
      else if (lower != null && upper == null) range = `> ${Math.round(lower)} W`;
      else if (lower != null && upper != null) range = `${Math.round(lower)}–${Math.round(upper)} W`;
      else range = '—';
      return { name: `${z.zone_code} ${z.zone_name}`, color: z.color_hex, range };
    });
  }

  onAnchorEdit(field: 'maxlass' | 'vo2max', value: number | null): void {
    // Leeres/0-Feld = kein Override -> naechste Preview re-seedet den Auto-Wert.
    const overridden = value != null && value > 0;
    if (field === 'maxlass') {
      this.maxlassEdit = value;
      this.maxlassOverridden = overridden;
    } else {
      this.vo2maxPowerEdit = value;
      this.vo2maxOverridden = overridden;
    }
    this.preview$.next();
  }

  onDateChange(): void {
    this.preview$.next();
  }

  private overridePayload(): { maxlass_override_w?: number; vo2max_power_override_w?: number } {
    const p: { maxlass_override_w?: number; vo2max_power_override_w?: number } = {};
    if (this.maxlassOverridden && this.maxlassEdit != null && this.maxlassEdit > 0) {
      p.maxlass_override_w = this.maxlassEdit;
    }
    if (this.vo2maxOverridden && this.vo2maxPowerEdit != null && this.vo2maxPowerEdit > 0) {
      p.vo2max_power_override_w = this.vo2maxPowerEdit;
    }
    return p;
  }

  private applyPreviewSuccess(res: AutoZonesPreviewResponse): void {
    this.previewLoading = false;
    this.previewError = false;
    this.previewFitQuality = res.fit_quality_pct ?? null;
    // Unplausible Zonen (z.B. Z5 < Z4) -> Hinweis anzeigen und Anwenden sperren,
    // damit der Nutzer den 409 nicht erst beim Klick erlebt.
    this.previewImplausible = res.zones_payload?.monotonic === false;
    this.previewRows = res.zones_payload?.zones
      ? this.mapZones(res.zones_payload.zones)
      : null;
    // Nicht-ueberschriebene Anker aus der Preview re-seeden, damit Auto-CP-Spalte
    // + Δ zum gewaehlten effective_from passen. Programmatische Zuweisung feuert
    // kein ngModelChange -> keine Rueckkopplung in die Preview-Pipeline.
    if (!this.maxlassOverridden && res.maxlass_w != null) {
      this.maxlassEdit = Math.round(res.maxlass_w);
    }
    if (!this.vo2maxOverridden && res.vo2max_power_w != null) {
      this.vo2maxPowerEdit = Math.round(res.vo2max_power_w);
    }
    this.cdr.markForCheck();
  }

  openSettings(): void {
    this.dialogRef.close({ navigated: true });
    // Vorher '/user-settings' — diese Route existiert nicht und landete über die
    // Wildcard auf dem Dashboard. Jetzt direkt in den Einstellungs-Reiter des
    // Trainingsbereiche-Cockpits.
    this.router.navigate(['/analyse/diagnostik/trainingsbereiche'], {
      queryParams: { tab: 'einstellungen' },
    });
  }

  dismiss(): void {
    if (this.busy || this.processing) return;
    this.busy = true;
    this.analytics.resolveZoneDeviation(this.data.notice.id, { action: 'dismiss' })
      .subscribe({
        next: (res) => this.dialogRef.close({ resolved: true, status: res.status }),
        error: () => { this.busy = false; this.cdr.markForCheck(); },
      });
  }

  applyAuto(resolution?: 'keep_later' | 'supersede'): void {
    if (this.busy || this.processing || !this.effectiveFromValid()) return;
    const dateIso = this.toIsoDate(this.effectiveFrom);
    this.busy = true;
    this.applyError = false;
    this.cdr.markForCheck();

    this.analytics.resolveZoneDeviation(this.data.notice.id, {
      action: 'apply_auto',
      effective_from: dateIso,
      ...(resolution ? { resolution } : {}),
      ...this.overridePayload(),
    }).subscribe({
      next: () => {
        this.busy = false;
        this.conflict = null;
        this.startPolling();
      },
      error: (err) => {
        this.busy = false;
        if (err?.status === 409 && err?.error?.conflict) {
          // Rück-Datierungs-Konflikt → Nutzer fragen.
          this.conflict = err.error.overlapping_sets ?? [];
        } else {
          this.applyError = true;
        }
        this.cdr.markForCheck();
      },
    });
  }

  private startPolling(): void {
    this.processing = true;
    this.cdr.markForCheck();
    let ticks = 0;
    this.pollSub = interval(1500).pipe(
      switchMap(() => this.analytics.getZoneDeviationStatus(this.data.notice.id)),
    ).subscribe({
      next: (s) => {
        ticks++;
        // Status-Spalte fehlt (Migration nicht eingespielt) → Fire-and-forget:
        // nach kurzer Wartezeit schließen, Recompute läuft im Hintergrund.
        if (s.compute_tracking === false) {
          if (ticks >= 2) this.finishProcessing();
          return;
        }
        const cs = s.resolved_compute_status;
        if (cs === 'done') {
          this.finishProcessing();
        } else if (cs === 'failed') {
          this.processing = false;
          this.applyError = true;
          this.pollSub?.unsubscribe();
          this.cdr.markForCheck();
        } else if (ticks > 80) {
          // ~2 min Timeout: schließen, Hintergrund-Recompute läuft weiter.
          this.finishProcessing();
        }
      },
      error: () => {
        ticks++;
        if (ticks > 10) this.finishProcessing();
      },
    });
  }

  private finishProcessing(): void {
    this.pollSub?.unsubscribe();
    this.dialogRef.close({
      resolved: true,
      status: 'resolved',
      effective_from: this.toIsoDate(this.effectiveFrom),
    });
  }

  private toIsoDate(d: Date): string {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }
}
